import './index.css'
import cn from 'clsx'
import React from 'react'
import Icon, { IconKey } from 'components/ui/icon'
export type LozengeColor =
  | 'blue'
  | 'bold-blue'
  | 'outline-blue'
  | 'gray'
  | 'bold-gray'
  | 'green'
  | 'bold-green'
  | 'purple'
  | 'bold-purple'
  | 'red'
  | 'bold-red'
  | 'yellow'
  | 'bold-yellow'
type Props = {
  color?: LozengeColor
  icon?: IconKey
  strikethrough?: boolean
  bold?: boolean
  text: React.ReactNode | null | undefined
  large?: boolean
  nodeRight?: React.ReactNode | null | undefined
}

/**
 * A lozenge (badge/pill) component for displaying status, tags, or categorical information.
 * Supports various colors, icons, and styling options with customizable content.
 *
 * @param color - The color theme of the lozenge (default: 'gray')
 * @param icon - Optional icon to display in the lozenge
 * @param strikethrough - Whether to apply strikethrough text styling
 * @param bold - Whether to apply bold text styling
 * @param text - The main text content to display
 * @param large - Whether to use large sizing
 * @param nodeRight - Optional content to display on the right side
 * @returns A styled lozenge component with the specified appearance and content
 */
const Lozenge = ({
  color = 'gray',
  icon,
  strikethrough,
  bold,
  text,
  large,
  nodeRight,
}: Props) => {
  const iconSize: number = text ? 12 : 16
  return (
    <span
      className={cn(
        'lozenge',
        {
          'lozenge--blue': color === 'blue',
          'lozenge--bold-blue': color === 'bold-blue',
          'lozenge--outline-blue': color === 'outline-blue',
          'lozenge--gray': color === 'gray',
          'lozenge--bold-gray': color === 'bold-gray',
          'lozenge--green': color === 'green',
          'lozenge--bold-green': color === 'bold-green',
          'lozenge--purple': color === 'purple',
          'lozenge--bold-purple': color === 'bold-purple',
          'lozenge--red': color === 'red',
          'lozenge--bold-red': color === 'bold-red',
          'lozenge--yellow': color === 'yellow',
          'lozenge--bold-yellow': color === 'bold-yellow',
        },
        {
          'lozenge--icon-only': icon && !text,
          'lozenge--strikethrough': strikethrough,
          'lozenge--bold': bold,
          'lozenge--large': large,
        },
      )}
    >
      <span className="lozenge-content">
        {icon && (
          <span className="lozenge-icon">
            <Icon type={icon} width={iconSize} height={iconSize} />
          </span>
        )}
        {text && <span className="lozenge-text">{text}</span>}
        {nodeRight && <span className="lozenge-node-right">{nodeRight}</span>}
      </span>
    </span>
  )
}

export default React.memo<Props>(Lozenge)
